import { useEffect, useState } from 'react';
import { useImageStore } from '../stores/imageStore';

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M19 19L14.667 14.667M17 9.5C17 13.6421 13.6421 17 9.5 17C5.35786 17 2 13.6421 2 9.5C2 5.35786 5.35786 2 9.5 2C13.6421 2 17 5.35786 17 9.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 4H14M5.333 4V2.667C5.333 2.298 5.632 2 6 2H10C10.368 2 10.667 2.298 10.667 2.667V4M12.667 4V13.333C12.667 13.702 12.368 14 12 14H4C3.632 14 3.333 13.702 3.333 13.333V4" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MetricCard = ({ title, value, subValue, color = 'blue' }) => {
  const colorClasses = {
    blue: 'from-blue-600/20 to-blue-600/5 border-blue-600/30',
    purple: 'from-purple-600/20 to-purple-600/5 border-purple-600/30',
    green: 'from-green-600/20 to-green-600/5 border-green-600/30',
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-xl border p-5`}>
      <div className="text-sm text-text-secondary mb-1">{title}</div>
      <div className="text-3xl font-bold text-text-primary">{value}</div>
      {subValue && <div className="text-xs text-text-muted mt-1">{subValue}</div>}
    </div>
  );
};

export const Images = () => {
  const { stats, list, isLoading, error, fetchStats, fetchList, deleteImage } = useImageStore();
  const [page, setPage] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [searchVkUserId, setSearchVkUserId] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  const limit = 20;

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchList(page, limit, activeFilter);
  }, [page, activeFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = searchVkUserId.trim();
    setActiveFilter(trimmed || null);
    setPage(1);
  };

  const handleReset = () => {
    setSearchVkUserId('');
    setActiveFilter(null);
    setPage(1);
  };

  const handleDelete = async (id) => {
    if (!confirm('Удалить это изображение?')) return;
    await deleteImage(id);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (error) {
    return (
      <div className="bg-error/10 border border-error rounded-xl p-4 text-error">
        <p className="font-medium">Ошибка загрузки данных</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Сгенерированные изображения</h1>
        <p className="text-text-secondary mt-1">Просмотр и управление изображениями, созданными пользователями</p>
      </div>

      {/* Метрики */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            title="Всего изображений"
            value={stats.total}
            color="blue"
          />
          <MetricCard
            title="За 24 часа"
            value={stats.last_24h}
            color="purple"
          />
          <MetricCard
            title="За 7 дней"
            value={stats.last_7d}
            color="green"
          />
        </div>
      )}

      {/* Топ промпты */}
      {stats && stats.top_prompts && stats.top_prompts.length > 0 && (
        <div className="bg-bg-secondary rounded-xl border border-border-color">
          <div className="p-6 border-b border-border-color">
            <h3 className="text-lg font-semibold text-text-primary">Топ запросы</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-bg-tertiary border-b border-border-color">
                <tr>
                  <th className="text-left text-xs font-medium text-text-secondary px-6 py-3 w-16">#</th>
                  <th className="text-left text-xs font-medium text-text-secondary px-6 py-3 w-24">Кол-во</th>
                  <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Запрос</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-color">
                {stats.top_prompts.map((item, index) => (
                  <tr key={index} className="hover:bg-bg-tertiary/50 transition-colors">
                    <td className="px-6 py-3 text-sm text-text-muted font-bold">
                      {index + 1}
                    </td>
                    <td className="px-6 py-3 text-sm font-bold text-accent-primary">
                      {item.count}
                    </td>
                    <td className="px-6 py-3 text-sm text-text-secondary">
                      {item.prompt}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Топ пользователи */}
      {stats && stats.top_users && stats.top_users.length > 0 && (
        <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Топ пользователи</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {stats.top_users.map((user, index) => (
              <div
                key={user.vk_user_id}
                className="text-center cursor-pointer hover:bg-bg-tertiary rounded-lg p-3 transition-colors"
                onClick={() => {
                  setSearchVkUserId(user.vk_user_id);
                  setActiveFilter(user.vk_user_id);
                  setPage(1);
                }}
              >
                <div className="text-2xl font-bold text-text-primary mb-1">{user.image_count}</div>
                <div className="text-xs text-text-secondary truncate">VK ID: {user.vk_user_id}</div>
                <div className="text-xs text-text-muted mt-1">#{index + 1}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Поиск по VK ID */}
      <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Поиск по пользователю</h3>
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="flex-1 relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">
              <SearchIcon />
            </div>
            <input
              type="text"
              value={searchVkUserId}
              onChange={(e) => setSearchVkUserId(e.target.value)}
              placeholder="Введите VK ID пользователя..."
              className="w-full pl-10 pr-4 py-2.5 bg-bg-tertiary border border-border-color rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-primary/50 focus:border-accent-primary"
            />
          </div>
          <button
            type="submit"
            className="px-6 py-2.5 bg-accent-primary text-white font-medium rounded-lg hover:bg-accent-primary/90 transition-colors"
          >
            Найти
          </button>
          {activeFilter && (
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2.5 bg-bg-tertiary text-text-secondary font-medium rounded-lg hover:text-text-primary transition-colors flex items-center gap-2"
            >
              <CloseIcon />
              Сбросить
            </button>
          )}
        </form>
        {activeFilter && (
          <div className="mt-3 text-sm text-text-secondary">
            Фильтр: <span className="text-accent-primary font-mono">{activeFilter}</span>
            {list && <span className="ml-2">— найдено {list.total} изображений</span>}
          </div>
        )}
      </div>

      {/* Сетка изображений */}
      <div className="bg-bg-secondary rounded-xl border border-border-color">
        <div className="p-6 border-b border-border-color flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">
            {activeFilter ? 'Результаты поиска' : 'Последние изображения'}
          </h3>
        </div>

        {isLoading && !list ? (
          <div className="p-8 flex items-center justify-center">
            <div className="animate-spin h-6 w-6 border-2 border-accent-primary border-t-transparent rounded-full" />
          </div>
        ) : list && list.items && list.items.length > 0 ? (
          <>
            <div className="p-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {list.items.map((item) => (
                  <div
                    key={item.id}
                    className="group relative bg-bg-tertiary rounded-lg border border-border-color/50 overflow-hidden hover:border-accent-primary/50 transition-colors"
                  >
                    {/* Изображение */}
                    <div
                      className="aspect-square bg-gray-800 cursor-pointer relative"
                      onClick={() => setSelectedImage(item)}
                    >
                      <img
                        src={item.img_url}
                        alt={item.prompt || 'Generated'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full text-text-muted text-xs">Ошибка загрузки</div>';
                        }}
                      />
                      {/* Оверлей при наведении */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImage(item);
                          }}
                          className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-xs rounded-lg transition-colors"
                        >
                          Просмотр
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item.id);
                          }}
                          className="p-1.5 bg-error/60 hover:bg-error text-white rounded-lg transition-colors"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </div>

                    {/* Инфо */}
                    <div className="p-3">
                      <div className="text-xs text-text-secondary truncate" title={item.prompt}>
                        {item.prompt || '—'}
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-text-muted font-mono">
                          {item.vk_user_id}
                        </span>
                        <span className="text-xs text-text-muted">
                          {formatDate(item.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Пагинация */}
            {list.total_pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-border-color">
                <div className="text-sm text-text-secondary">
                  Страница {list.page} из {list.total_pages} ({list.total} изображений)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-2 rounded-lg bg-bg-tertiary text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeftIcon />
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(list.total_pages, p + 1))}
                    disabled={page >= list.total_pages}
                    className="p-2 rounded-lg bg-bg-tertiary text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRightIcon />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-text-secondary">
            {activeFilter ? 'Нет изображений у этого пользователя' : 'Нет сгенерированных изображений'}
          </div>
        )}
      </div>

      {/* Модальное окно просмотра */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="max-w-4xl max-h-full bg-bg-secondary rounded-xl border border-border-color overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Изображение */}
            <div className="flex items-center justify-center p-4 bg-gray-900">
              <img
                src={selectedImage.img_url}
                alt={selectedImage.prompt || 'Generated'}
                className="max-h-[70vh] max-w-full object-contain"
              />
            </div>

            {/* Инфо */}
            <div className="p-6 space-y-4">
              {selectedImage.prompt && (
                <div>
                  <div className="text-sm text-text-secondary mb-1">Запрос:</div>
                  <div className="text-text-primary">{selectedImage.prompt}</div>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-text-secondary">VK ID:</span>{' '}
                  <span className="text-sm font-mono text-text-primary">{selectedImage.vk_user_id}</span>
                </div>
                <div>
                  <span className="text-sm text-text-secondary">{formatDate(selectedImage.created_at)}</span>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <a
                  href={selectedImage.img_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-accent-primary text-white text-sm font-medium rounded-lg hover:bg-accent-primary/90 transition-colors"
                >
                  Открыть оригинал
                </a>
                <button
                  onClick={() => {
                    handleDelete(selectedImage.id);
                    setSelectedImage(null);
                  }}
                  className="px-4 py-2 bg-error text-white text-sm font-medium rounded-lg hover:bg-error/90 transition-colors"
                >
                  Удалить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
